// table.mapper.ts
// 평가 리포트 테이블용 데이터 트랜스폼 함수

import { usePersistStore } from '@/shared/lib/store';
import { isVisibleClassStudent } from '@/shared/utils/publish';

interface ReportTableStudent {
    stntId: string;
    name: string;
    scoreText: string;
    answers: ('correct' | 'incorrect' | 'partial' | 'not-submitted' | 'concept' | 'activity')[];
    submAt: string; // submAt 값 추가
    userNum?: number;
}

export const getReportClassMembers = (userId: string, classSeq?: number | string) => {
    const { sMyClassMemberList, memberList } = usePersistStore.getState();
    const targetClassSeq = classSeq?.toString() || userId?.split('-')[1];
    const targetClass = sMyClassMemberList?.find(
        ({ classInfo }) => classInfo.classSeq?.toString() === targetClassSeq
    );

    return targetClass?.memberInfo ?? memberList ?? [];
};

export const findClassMember = (userId: string, classSeq?: number | string) => {
    const targetId = userId?.split('-')[0];
    return getReportClassMembers(userId, classSeq).find(
        (member) => member.orgMemberId?.split('M')[0] === targetId
    );
};

export const findVisibleClassMember = (userId: string, classSeq?: number | string) => {
    const member = findClassMember(userId, classSeq);
    return isVisibleClassStudent(member) ? member : undefined;
};

export const findVisibleClassUserNum = (userId: string, classSeq?: number | string) => {
    const targetId = userId?.split('-')[0];
    const visibleMembers = getReportClassMembers(userId, classSeq)
        .filter(isVisibleClassStudent)
        .sort((a, b) => a.schClassNo - b.schClassNo);
    const index = visibleMembers.findIndex(
        (member) => member.orgMemberId?.split('M')[0] === targetId
    );

    return index >= 0 ? index + 1 : undefined;
};

export const findUserName = (userId: string) => {
    const memberList = usePersistStore?.getState()?.memberList;
    const targetId = userId?.split('-')[0];
    const user = memberList?.find((user) => user.orgMemberId?.split('M')[0] === targetId);
    if (user) {
        return user.name;
    }
};

export const findUserNum = (userId: string) => {
    const memberList = usePersistStore?.getState()?.memberList;
    const targetId = userId?.split('-')[0];
    const user = memberList?.find((user) => user.orgMemberId?.split('M')[0] === targetId);
    if (user) {
        return user.schClassNo;
    }
};

export function mapEvalReportTableData(
    data: API.REPORT.TCH.EvalScoringReport.Res,
    classStntUserName?: string,
    type?: string,
    classSeq?: number | string
): {
    questionHeaders: string[];
    avgRates: (string | number)[];
    avgSolveTimes: string[];
    students: ReportTableStudent[];
    type?: string;
} {
    const questionHeaders = data.mdulEvlInfoList.map(
        (_: API.REPORT.DATA.HwMdulEvlInfo, idx: number) => (idx + 1).toString()
    );
    const avgRates = data.mdulEvlInfoList.map(
        (item: API.REPORT.DATA.HwMdulEvlInfo) => item.classAvgCorrectRate
    );
    const avgSolveTimes = data.mdulEvlInfoList.map((item: API.REPORT.DATA.HwMdulEvlInfo) =>
        String(item.classAvgSolvingTime)
    );
    // 중복 ID는 기존 find와 동일하게 첫 번째 항목을 사용합니다.
    const membersById = new Map<string, API.MyClassMemberList.MemberInfo>();
    for (const member of usePersistStore.getState().memberList ?? []) {
        const id = member.orgMemberId?.split('M')[0];
        if (!membersById.has(id)) membersById.set(id, member);
    }
    const answersByQuestion = data.mdulEvlInfoList.map((question) => {
        const answersByUserId = new Map<string, API.REPORT.DATA.StntEvlInfo>();
        for (const answer of question.stntEvlInfoList) {
            if (!answersByUserId.has(answer.userId)) answersByUserId.set(answer.userId, answer);
        }
        return answersByUserId;
    });
    const students = data.stntEvlScrInfoList.map((student: API.REPORT.DATA.HwStntEvlScrInfo) => {
        const member =
            type === 'class'
                ? findClassMember(student.userId, classSeq)
                : membersById.get(student.userId?.split('-')[0]);
        const answers: ReportTableStudent['answers'] = answersByQuestion.map((answersByUserId) => {
            const stnt = answersByUserId.get(student.userId);
            if (!stnt) return 'not-submitted';
            if (stnt.stntEvlResult?.submAt === 'N') return 'not-submitted';
            if (stnt.stntEvlResult?.errata === 4 && stnt.stntEvlResult?.mrkTy === 2)
                return 'activity';
            if (stnt.stntEvlResult?.errata === 4 && stnt.stntEvlResult?.mrkTy === 3)
                return 'concept';
            if (stnt.stntEvlResult?.errata === 1) return 'correct';
            if (stnt.stntEvlResult?.errata === 2) return 'incorrect';
            if (stnt.stntEvlResult?.errata === 3) return 'partial';
            return 'incorrect';
        });
        const _name = classStntUserName || member?.name || student.flnm;
        return {
            name: _name,
            stntId: student.userId,
            scoreText: `${student.evlResultScr}점(${student.evlResultAnctNm})`,
            answers,
            submAt: student?.submAt, // submAt 값 추가
            userNum:
                type === 'class'
                    ? findVisibleClassUserNum(student.userId, classSeq)
                    : member?.schClassNo,
        };
    });

    const classStudents = students
        .filter((student) => !!findVisibleClassMember(student.stntId, classSeq)?.name)
        ?.sort((a, b) => (a.userNum ?? 0) - (b.userNum ?? 0));

    return {
        questionHeaders,
        avgRates,
        avgSolveTimes,
        students: type === 'class' ? classStudents : students,
    };
}
